/**
 * useRecentGenerations — loads the recent finished generations for the YouTube
 * "publish from a generation" picker.
 *
 * Extracted verbatim from YouTubePublishForm (Stage 06 god-component split):
 * owns the `recentGenerations` list and its cancellable load effect. Only loads
 * in `end_to_end` mode.
 *
 * On fetch failure the list stays empty AND `loadError` is set so callers can
 * render a distinct "couldn't load" message instead of silently showing nothing.
 */

import { useEffect, useState } from 'react';
import { listRecentGenerations } from '../services/youtubePublishApi';
import type { RecentGeneration, YouTubePublishMode } from '../services/youtubePublishApi';

export interface UseRecentGenerationsResult {
  recentGenerations: RecentGeneration[];
  loadError: string | null;
}

export function useRecentGenerations(mode: YouTubePublishMode): UseRecentGenerationsResult {
  const [recentGenerations, setRecentGenerations] = useState<RecentGeneration[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'end_to_end') return;
    let cancelled = false;
    setLoadError(null);
    listRecentGenerations()
      .then((gens) => {
        if (!cancelled) {
          setRecentGenerations(gens);
          setLoadError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRecentGenerations([]);
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  return { recentGenerations, loadError };
}
